import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, AlertCircle, X, PackagePlus, Check, AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Lakshadweep', 'Puducherry'
];

const emptyLineItem = () => ({
  key: Date.now() + Math.random(),
  product_id: '',
  serial_number: '',
  description: '',
  mrp: '',
  quantity: 1,
  unit_id: '',
  price_per_unit: '',
  discount_percentage: 0,
  discount_amount: 0,
  tax_id: '',
  tax_amount: 0,
  total_amount: 0,
  _stock_count: null,
  _assigned_item_ids: [],
  _items_confirmed: false,
});

export const DeliveryChallanCreate = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [units, setUnits] = useState([]);

  const [customerId, setCustomerId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [challanNumber, setChallanNumber] = useState('');
  const [challanDate, setChallanDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [stateOfSupply, setStateOfSupply] = useState('');

  const [lineItems, setLineItems] = useState([emptyLineItem()]);
  const [description, setDescription] = useState('');
  const [roundOff, setRoundOff] = useState(true);

  // Item selection modal
  const [itemModalIdx, setItemModalIdx] = useState(null);
  const [itemModalIds, setItemModalIds] = useState([]);
  const [itemModalAvailable, setItemModalAvailable] = useState([]);
  const [itemModalErrors, setItemModalErrors] = useState([]);
  const [itemModalValidating, setItemModalValidating] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [customersRes, companiesRes, productsRes, taxesRes, unitsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/customers`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders())
        ]);
        setCustomers(customersRes.data.filter(c => c.active));
        setCompanies(companiesRes.data.filter(c => c.active));
        setProducts(productsRes.data);
        setTaxes(taxesRes.data.filter(t => t.active));
        setUnits(unitsRes.data.filter(u => u.active));
      } catch (err) { setError('Failed to load master data'); }
    };
    fetchMasterData();
  }, []);

  const recalcLine = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.price_per_unit) || 0;
    const discPct = parseFloat(item.discount_percentage) || 0;
    const lineTotal = qty * price;
    const discAmt = lineTotal * discPct / 100;
    const taxableAmount = lineTotal - discAmt;
    const tax = taxes.find(t => t.id === item.tax_id);
    const taxPct = tax ? parseFloat(tax.percentage) : 0;
    const taxAmt = taxableAmount * taxPct / 100;
    const totalAmt = taxableAmount + taxAmt;
    return {
      ...item,
      discount_amount: Math.round(discAmt * 100) / 100,
      tax_amount: Math.round(taxAmt * 100) / 100,
      total_amount: Math.round(totalAmt * 100) / 100
    };
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'product_id') {
        const prod = products.find(p => p.id === value);
        if (prod && prod.unit_id) updated[index].unit_id = prod.unit_id;
        updated[index]._assigned_item_ids = [];
        updated[index]._items_confirmed = false;
        updated[index]._stock_count = null;
        if (value) {
          axios.get(`${BACKEND_URL}/api/inventory/${value}/available-items`, getAuthHeaders())
            .then(res => {
              setLineItems(p => {
                const u = [...p];
                const li = u.find(i => i.key === updated[index].key);
                if (li) li._stock_count = res.data.stock_count;
                return u;
              });
            }).catch(() => {});
        }
        if (value && !updated[updated.length - 1]?.product_id) return updated.map(i => recalcLine(i));
        if (value) updated.push(emptyLineItem());
      }
      if (field === 'quantity') {
        updated[index]._assigned_item_ids = [];
        updated[index]._items_confirmed = false;
      }
      updated[index] = recalcLine(updated[index]);
      return updated;
    });
  };

  const addLineItem = () => setLineItems(prev => [...prev, emptyLineItem()]);
  const removeLineItem = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  // Item selection modal
  const openItemModal = async (idx) => {
    const li = lineItems[idx];
    if (!li.product_id) return;
    const qty = parseInt(li.quantity) || 1;
    setItemModalIdx(idx);
    setItemModalIds(li._assigned_item_ids.length === qty ? [...li._assigned_item_ids] : Array(qty).fill(''));
    setItemModalErrors([]);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/inventory/${li.product_id}/available-items`, getAuthHeaders());
      setItemModalAvailable(res.data.items || []);
    } catch { setItemModalAvailable([]); }
  };

  const validateAndConfirmItems = async () => {
    const li = lineItems[itemModalIdx];
    const ids = itemModalIds.map(s => s.trim()).filter(Boolean);
    if (ids.length !== itemModalIds.length) {
      setItemModalErrors(['All item ID fields must be filled']);
      return;
    }
    const dupes = ids.filter((v, i, a) => a.indexOf(v) !== i);
    if (dupes.length) {
      setItemModalErrors([`Duplicate item IDs: ${[...new Set(dupes)].join(', ')}`]);
      return;
    }
    setItemModalValidating(true);
    setItemModalErrors([]);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/inventory/validate-items`, {
        product_id: li.product_id, item_ids: ids
      }, getAuthHeaders());
      if (res.data.valid) {
        setLineItems(prev => {
          const u = [...prev];
          u[itemModalIdx] = { ...u[itemModalIdx], _assigned_item_ids: ids, _items_confirmed: true };
          return u;
        });
        setItemModalIdx(null);
      } else {
        setItemModalErrors(res.data.errors || ['Validation failed']);
      }
    } catch { setItemModalErrors(['Failed to validate items']); }
    finally { setItemModalValidating(false); }
  };

  const allItemsConfirmed = lineItems.filter(li => li.product_id).every(li => {
    const qty = parseInt(li.quantity) || 0;
    if (qty <= 0) return true;
    if (li._stock_count === 0 || li._stock_count === null) return true;
    return li._items_confirmed && li._assigned_item_ids.length === qty;
  });

  const subtotal = lineItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);
  const roundOffAmount = roundOff ? Math.round(subtotal) - subtotal : 0;
  const total = roundOff ? Math.round(subtotal) : Math.round(subtotal * 100) / 100;
  const totalQty = lineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const totalDiscount = lineItems.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const totalTax = lineItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0);

  const handleSave = async () => {
    setError('');
    if (!customerId) { setError('Please select a customer (party)'); return; }
    if (!lineItems.some(li => li.product_id)) { setError('Please add at least one product'); return; }
    if (!allItemsConfirmed) { setError('Please select item IDs for all products with inventory'); return; }

    setSubmitting(true);
    try {
      const payload = {
        customer_id: customerId,
        company_id: companyId || null,
        challan_number: challanNumber,
        challan_date: challanDate,
        due_date: dueDate || null,
        state_of_supply: stateOfSupply,
        line_items: lineItems.filter(li => li.product_id).map(li => ({
          product_id: li.product_id,
          description: li.description,
          mrp: parseFloat(li.mrp) || null,
          quantity: parseFloat(li.quantity) || 0,
          unit_id: li.unit_id || null,
          price_per_unit: parseFloat(li.price_per_unit) || 0,
          discount_percentage: parseFloat(li.discount_percentage) || 0,
          discount_amount: li.discount_amount,
          tax_id: li.tax_id || null,
          tax_amount: li.tax_amount,
          total_amount: li.total_amount,
          assigned_item_ids: li._assigned_item_ids.length > 0 ? li._assigned_item_ids : null
        })),
        description: description || null,
        round_off: roundOff,
        round_off_amount: Math.round(roundOffAmount * 100) / 100,
        subtotal: Math.round(subtotal * 100) / 100,
        total
      };
      await axios.post(`${BACKEND_URL}/api/delivery-challans`, payload, getAuthHeaders());
      navigate('/admin/sale/delivery-challan');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save delivery challan');
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="delivery-challan-create-page" className="pb-24">
      <button onClick={() => navigate('/admin/sale/delivery-challan')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Delivery Challans
      </button>

      <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight mb-8">Delivery Challan</h1>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* HEADER */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Party (Customer) <span className="text-red-500">*</span></label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="select-customer"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Customer</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` (${c.phone})` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="select-company"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Challan No.</label>
            <input type="text" value={challanNumber} onChange={(e) => setChallanNumber(e.target.value)} placeholder="e.g., DC/25-26/52"
              data-testid="input-challan-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
            <input type="date" value={challanDate} onChange={(e) => setChallanDate(e.target.value)}
              data-testid="input-challan-date" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
              data-testid="input-due-date" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">State of Supply</label>
            <select value={stateOfSupply} onChange={(e) => setStateOfSupply(e.target.value)} data-testid="select-state"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select State</option>
              {STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* LINE ITEMS */}
      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
          <button onClick={addLineItem} data-testid="add-line-item-btn"
            className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm">
            <Plus className="h-4 w-4" /> Add Row
          </button>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[1200px]">
            <div className="grid gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider"
              style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.5fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr' }}>
              <div>#</div>
              <div>Product</div>
              <div>MRP</div>
              <div>Qty</div>
              <div></div>
              <div>Unit</div>
              <div>Price/Unit</div>
              <div>Disc %</div>
              <div>Disc Amt</div>
              <div>Tax</div>
              <div>Tax Amt</div>
              <div>Amount</div>
            </div>
            {lineItems.map((item, idx) => (
              <div key={item.key} data-testid={`line-item-${idx}`}
                className="grid gap-2 px-4 py-2 border-b border-slate-100 items-start"
                style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.5fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr' }}>
                <div className="flex items-center gap-1 pt-2">
                  <span className="text-slate-500 text-sm">{idx + 1}</span>
                  {lineItems.length > 1 && (
                    <button onClick={() => removeLineItem(idx)} data-testid={`remove-line-${idx}`}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"><Trash2 className="h-3 w-3" /></button>
                  )}
                </div>
                <div>
                  <select value={item.product_id} onChange={(e) => updateLineItem(idx, 'product_id', e.target.value)}
                    data-testid={`line-product-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">Select</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  {item.product_id && item._stock_count !== null && (
                    <span className={`block text-xs mt-0.5 px-1 ${item._stock_count > 0 ? 'text-green-600' : 'text-red-500'}`}>
                      Stock: {item._stock_count}
                    </span>
                  )}
                  <input type="text" value={item.serial_number} onChange={(e) => updateLineItem(idx, 'serial_number', e.target.value)}
                    placeholder="Serial Number" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                  <input type="text" value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                    placeholder="Description" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <input type="number" value={item.mrp} onChange={(e) => updateLineItem(idx, 'mrp', e.target.value)}
                    placeholder="0" className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <input type="number" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                    min="0" data-testid={`line-qty-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="pt-1">
                  {item.product_id && item._stock_count > 0 && (
                    <button type="button" onClick={() => openItemModal(idx)} data-testid={`line-items-btn-${idx}`}
                      className={`p-1.5 rounded transition-colors ${item._items_confirmed ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-orange-500 bg-orange-50 hover:bg-orange-100'}`}
                      title={item._items_confirmed ? `${item._assigned_item_ids.length} items selected` : 'Select items for DC'}>
                      {item._items_confirmed ? <Check className="h-4 w-4" /> : <PackagePlus className="h-4 w-4" />}
                    </button>
                  )}
                </div>
                <div>
                  <select value={item.unit_id} onChange={(e) => updateLineItem(idx, 'unit_id', e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">-</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <input type="number" value={item.price_per_unit} onChange={(e) => updateLineItem(idx, 'price_per_unit', e.target.value)}
                    placeholder="0" step="0.01" data-testid={`line-price-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <input type="number" value={item.discount_percentage} onChange={(e) => updateLineItem(idx, 'discount_percentage', e.target.value)}
                    min="0" max="100" step="0.01"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div><span className="text-sm text-slate-600 px-2">{item.discount_amount?.toFixed(2)}</span></div>
                <div>
                  <select value={item.tax_id} onChange={(e) => updateLineItem(idx, 'tax_id', e.target.value)}
                    data-testid={`line-tax-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">-</option>
                    {taxes.map(t => <option key={t.id} value={t.id}>{t.name} ({t.percentage}%)</option>)}
                  </select>
                </div>
                <div><span className="text-sm text-slate-600 px-2">{item.tax_amount?.toFixed(2)}</span></div>
                <div><span className="text-sm font-medium text-slate-900 px-2">{item.total_amount?.toFixed(2)}</span></div>
              </div>
            ))}
            <div className="grid gap-2 px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm font-semibold"
              style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.5fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr' }}>
              <div></div>
              <div className="text-slate-700">TOTAL</div>
              <div></div>
              <div className="text-slate-700">{totalQty}</div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div className="text-slate-700">{totalDiscount.toFixed(2)}</div>
              <div></div>
              <div className="text-slate-700">{totalTax.toFixed(2)}</div>
              <div className="text-slate-900">{subtotal.toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Add Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                rows={2} placeholder="Add a note or description..."
                data-testid="input-description"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm resize-none" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)}
                  data-testid="round-off-checkbox" className="w-4 h-4 text-orange-500 border-slate-300 rounded" />
                <span className="text-sm text-slate-600">Round Off</span>
                {roundOff && <span className="text-sm text-slate-500">{roundOffAmount >= 0 ? '+' : ''}{roundOffAmount.toFixed(2)}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700">Total</span>
                <span className="text-lg font-bold text-slate-900 min-w-[100px] text-right" data-testid="total-amount">
                  ₹{total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SAVE */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between z-40">
        {!allItemsConfirmed && lineItems.some(li => li.product_id) && (
          <span className="flex items-center gap-1.5 text-amber-600 text-sm"><AlertTriangle className="h-4 w-4" /> Select item IDs for all products before saving</span>
        )}
        {(allItemsConfirmed || !lineItems.some(li => li.product_id)) && <span></span>}
        <button onClick={handleSave} disabled={submitting || !allItemsConfirmed} data-testid="save-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-8 py-2.5 rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
      </div>

      {/* ITEM SELECTION MODAL */}
      {itemModalIdx !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Select Item IDs for DC</h2>
              <button onClick={() => setItemModalIdx(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-1">
                Product: <span className="font-medium text-slate-900">{products.find(p => p.id === lineItems[itemModalIdx]?.product_id)?.name || '-'}</span>
              </p>
              <p className="text-sm text-slate-500 mb-4">
                Enter {itemModalIds.length} item ID(s) to assign to this DC. Available: {itemModalAvailable.length} in stock.
              </p>

              {itemModalErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4 text-sm">
                  {itemModalErrors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {itemModalIds.map((val, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-6">{i + 1}.</span>
                    <input
                      type="text" value={val}
                      onChange={e => { const n = [...itemModalIds]; n[i] = e.target.value; setItemModalIds(n); setItemModalErrors([]); }}
                      list={`dc-avail-items-${i}`}
                      placeholder="Enter or select item ID"
                      data-testid={`item-modal-input-${i}`}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono focus:border-orange-500 focus:outline-none"
                    />
                    <datalist id={`dc-avail-items-${i}`}>
                      {itemModalAvailable.map(a => <option key={a.item_id} value={a.item_id} />)}
                    </datalist>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button onClick={() => setItemModalIdx(null)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button onClick={validateAndConfirmItems} disabled={itemModalValidating || itemModalIds.some(v => !v.trim())}
                  data-testid="item-modal-confirm"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {itemModalValidating && <Loader2 className="h-4 w-4 animate-spin" />}
                  Validate & Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
